import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import TrackingScanService from './TrackingScanService';
import WeeklyShipService from '../../weeklyShip/WeeklyShipService';
import ImageModal from '../../weeklyShip/ImageModel';
import ProfileCombo from '../../../sharedComponents/ProfileCombo';
import { formatDate } from '../../../utils/dateUtils';

// Extracted from TrackingLookup so WeeklyShipMatch can open the held-items list too.

// ── FindOrderModal ────────────────────────────────────────────────────────────

export const FindOrderModal = ({ profiles, profilesReady, onSelect, onClose }) => {
  const [tab, setTab]               = useState('url');
  const [orders, setOrders]         = useState([]);
  const [urlQuery, setUrlQuery]     = useState('');
  const [loading, setLoading]       = useState(false);

  const handleProfileSelect = async (smileName) => {
    if (!smileName) return;
    setLoading(true);
    try {
      const { data } = await TrackingScanService.searchByName(smileName);
      setOrders(data);
    } catch { toast.error('โหลด order ไม่ได้'); }
    finally { setLoading(false); }
  };

  const handleUrlSearch = async () => {
    if (urlQuery.trim().length < 3) { toast.warning('พิมพ์อย่างน้อย 3 ตัวอักษร'); return; }
    setLoading(true);
    try {
      const { data } = await TrackingScanService.searchByUrl(urlQuery);
      setOrders(data);
    } catch { toast.error('ค้นหาไม่ได้'); }
    finally { setLoading(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[80vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h2 className="font-semibold text-gray-800">Find Order</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl leading-none">×</button>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 px-6 pt-4">
          {['customer', 'url'].map((t) => (
            <button
              key={t}
              onClick={() => { setTab(t); setOrders([]); }}
              className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-colors ${tab === t ? 'bg-indigo-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
            >
              {t === 'customer' ? 'By Customer' : 'By URL'}
            </button>
          ))}
        </div>

        {/* Search inputs */}
        <div className="relative z-20 px-6 py-4">
          {tab === 'customer' ? (
            profilesReady
              ? <ProfileCombo profiles={profiles} onChange={handleProfileSelect} showShop />
              : <p className="text-sm text-gray-400">กำลังโหลด profiles…</p>
          ) : (
            <div className="flex gap-2">
              <input
                type="text"
                value={urlQuery}
                onChange={(e) => setUrlQuery(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleUrlSearch()}
                placeholder="พิมพ์ส่วนหนึ่งของ URL..."
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-indigo-400"
              />
              <button
                onClick={handleUrlSearch}
                className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium rounded-lg"
              >
                ค้นหา
              </button>
            </div>
          )}
        </div>

        {/* Results */}
        <div className="flex-1 overflow-y-auto px-6 pb-4">
          {loading && <p className="text-sm text-gray-400 text-center py-4">กำลังโหลด…</p>}
          {!loading && orders.length === 0 && <p className="text-sm text-gray-300 text-center py-4">ไม่มีข้อมูล</p>}
          {!loading && orders.length > 0 && (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-xs text-gray-400 uppercase border-b border-gray-100">
                  <th className="text-left py-2">ID</th>
                  <th className="text-left py-2">Smile Name Input</th>
                  <th className="text-left py-2">URL</th>
                  <th className="text-left py-2">Shop</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {orders.map((o) => (
                  <tr key={o.id} className="border-b border-gray-50 hover:bg-gray-50">
                    <td className="py-2 text-gray-400 pr-3">#{o.id}</td>
                    <td className="py-2 font-medium text-gray-800 pr-3">{o.smileNameInput || '-'}</td>
                    <td className="py-2 text-gray-500 truncate max-w-[160px] pr-3">{o.url || '-'}</td>
                    <td className="py-2 text-xs text-indigo-600 pr-3">{o.CustomerProfile?.shop || '-'}</td>
                    <td className="py-2">
                      <button
                        onClick={() => onSelect(o)}
                        className="px-3 py-1 bg-emerald-600 hover:bg-emerald-700 text-white text-xs rounded-lg"
                      >
                        Select
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

// ── HeldItemsModal ────────────────────────────────────────────────────────────
// Lists parcels on hold (no shipDate yet). Promote one into a round: it takes that round's
// boxNo, its held weight is reused (no re-weigh), and the normal label is printed. After
// that it's a normal box in WeeklyShipMatch.
//
// deferPrint (WeeklyShipMatch, an admin machine with no label printer): promote flags the
// box labelPending instead of printing, and the รอพิมพ์ label tab is hidden — the warehouse
// prints from that tab on the TrackingLookup side and physically relabels the box.
const HeldItemsModal = ({ shipDates, profiles, profilesReady, onClose, deferPrint = false }) => {
  const [items, setItems]       = useState([]);
  const [loading, setLoading]   = useState(true);
  const [roundId, setRoundId]   = useState('');
  const [busyUuid, setBusyUuid] = useState(null);
  const [findForItem, setFindForItem] = useState(null); // held item we're identifying an order for
  const [zoomUrl, setZoomUrl]   = useState(null);
  const [tab, setTab]           = useState('held'); // 'held' | 'pending'
  const [pendingItems, setPendingItems] = useState([]);
  const [pendingLoading, setPendingLoading] = useState(false);
  const [pendingLoaded, setPendingLoaded] = useState(false);

  const openPendingTab = () => {
    setTab('pending');
    if (pendingLoaded) return;
    setPendingLoading(true);
    WeeklyShipService.getLabelPendingAirItems()
      .then((r) => { setPendingItems(r.data); setPendingLoaded(true); })
      .catch(() => toast.error('โหลดรายการรอพิมพ์ไม่ได้'))
      .finally(() => setPendingLoading(false));
  };

  // Print the normal box label for an admin-promoted box, then clear its flag — only
  // after the print server confirms, so a click on a printer-less machine leaves the
  // item in the queue instead of losing it.
  const printPending = async (item) => {
    setBusyUuid(item.uuid);
    try {
      const r = await fetch('http://localhost:5200/print', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          smileNameInput: item.smileNameInput || '',
          billableWeight: item.weight,
          shipDate: item.shipDate,
          trackingJp: item.trackingJp || '',
          orderId: item.orderId || '',
          boxNo: item.boxNo,
        }),
      });
      if (!r.ok) { toast.error('พิมพ์ label ไม่สำเร็จ'); setBusyUuid(null); return; }
    } catch {
      toast.error('พิมพ์ label ไม่ได้ — เปิด print server ก่อน');
      setBusyUuid(null);
      return;
    }
    try {
      await WeeklyShipService.markAirLabelPrinted(item.uuid);
    } catch {
      toast.warning('พิมพ์แล้ว แต่บันทึกสถานะไม่สำเร็จ — รายการจะยังค้างในลิสต์');
      setBusyUuid(null);
      return;
    }
    setPendingItems((prev) => prev.filter((it) => it.uuid !== item.uuid));
    toast.success(`พิมพ์ label กล่อง ${item.boxNo} แล้ว — แกะป้าย ${item.holdTag || ''} ออกแล้วติดป้ายใหม่`);
    setBusyUuid(null);
  };

  const airRounds = shipDates.filter(
    (d) => ['ODA J4410', 'D2U'].includes(d.warehouseReceivingAddress?.addressNickName)
  );

  useEffect(() => {
    WeeklyShipService.getHeldAirItems()
      .then((r) => setItems(r.data))
      .catch(() => toast.error('โหลดของที่พักไว้ไม่ได้'))
      .finally(() => setLoading(false));
  }, []);

  const roundShipDateStr = (id) => {
    const found = shipDates.find((d) => String(d.id) === String(id));
    if (!found?.shipDate) return '';
    return new Date(found.shipDate).toISOString().slice(0, 10);
  };

  // Promote a held box into the selected round. If an order is passed (found via Find Order),
  // its name is stamped onto the box + label so it looks like a normal scanned-found box; the
  // order↔box match itself still finalizes in WeeklyShipMatch.
  const promote = async (item, order = null) => {
    if (!roundId) { toast.warning('เลือกรอบเรือปลายทางก่อน'); return; }
    setBusyUuid(item.uuid);
    let boxNo;
    try {
      const res = await WeeklyShipService.promoteHeldAirItem({
        weeklyShipUuid: item.uuid,
        shipDateId: roundId,
        orderId: order?.id || undefined,
        smileNameInput: order?.smileNameInput || undefined,
        deferPrint: deferPrint || undefined,
      });
      boxNo = res.data.boxNo;
    } catch (err) {
      toast.error(err?.response?.data?.message || 'ย้ายเข้ารอบไม่สำเร็จ');
      setBusyUuid(null);
      return;
    }
    // Admin machine: no printer here — the box is flagged labelPending and the
    // warehouse prints it from the รอพิมพ์ label tab on TrackingLookup.
    if (deferPrint) {
      setItems((prev) => prev.filter((it) => it.uuid !== item.uuid));
      toast.success(`ย้าย ${item.holdTag} → กล่อง ${boxNo}${order?.smileNameInput ? ` (${order.smileNameInput})` : ''} — รอพิมพ์ label ที่โกดัง`);
      setBusyUuid(null);
      return;
    }
    try {
      const r = await fetch('http://localhost:5200/print', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          smileNameInput: order?.smileNameInput || '',
          billableWeight: item.weight,
          shipDate: roundShipDateStr(roundId),
          trackingJp: order?.trackingJp || item.trackingJp || '',
          orderId: order?.id || '',
          boxNo,
        }),
      });
      if (!r.ok) toast.error('พิมพ์ label ไม่สำเร็จ');
    } catch {
      toast.error('พิมพ์ label ไม่ได้ — เปิด print server ก่อน');
    }
    setItems((prev) => prev.filter((it) => it.uuid !== item.uuid));
    toast.success(`ย้าย ${item.holdTag} → กล่อง ${boxNo}${order?.smileNameInput ? ` (${order.smileNameInput})` : ''}`);
    setBusyUuid(null);
  };

  const openFind = (item) => {
    if (!roundId) { toast.warning('เลือกรอบเรือปลายทางก่อน'); return; }
    setFindForItem(item);
  };

  // Delete a held row entirely — for a false scan / wrong input. Reuses the air-cancel
  // endpoint: frees any bound photo back to the pending pool and removes the scan-history row.
  const removeHeld = async (item) => {
    if (!window.confirm(`ลบ ${item.holdTag} ? (ใช้กรณีสแกนผิด/กรอกผิด — รูปที่ผูกไว้จะถูกปล่อยกลับ)`)) return;
    setBusyUuid(item.uuid);
    try {
      await WeeklyShipService.cancelAirWeeklyShip(item.uuid);
      setItems((prev) => prev.filter((it) => it.uuid !== item.uuid));
      toast.success(`ลบ ${item.holdTag} แล้ว`);
    } catch (err) {
      toast.error(err?.response?.data?.message || 'ลบไม่สำเร็จ');
    }
    setBusyUuid(null);
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-3xl max-h-[85vh] flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h2 className="font-semibold text-gray-800">ของที่พักไว้</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl leading-none">×</button>
        </div>

        {/* Warehouse machine only: the print-queue tab. Admins (deferPrint) don't print. */}
        {!deferPrint && (
          <div className="flex gap-1 px-6 pt-3">
            <button
              onClick={() => setTab('held')}
              className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-colors ${tab === 'held' ? 'bg-amber-500 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
            >
              รอจัดรอบ
            </button>
            <button
              onClick={openPendingTab}
              className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-colors ${tab === 'pending' ? 'bg-amber-500 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
            >
              รอพิมพ์ label
            </button>
          </div>
        )}

        {tab === 'held' && (
        <>
        <div className="flex items-center gap-3 px-6 py-3 border-b border-gray-100 bg-gray-50">
          <label className="text-sm text-gray-600 shrink-0">ย้ายเข้ารอบ</label>
          <select
            value={roundId}
            onChange={(e) => setRoundId(e.target.value)}
            className="px-3 py-1.5 border border-gray-400 rounded-md text-sm"
          >
            <option value="">-- เลือกรอบเรือ --</option>
            {airRounds.map((d) => (
              <option key={d.id} value={d.id}>{formatDate(d.shipDate)}</option>
            ))}
          </select>
          <span className="text-xs text-gray-400 ml-auto">{items.length} รายการ</span>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-4">
          {loading && <p className="text-sm text-gray-400 text-center py-6">กำลังโหลด…</p>}
          {!loading && items.length === 0 && <p className="text-sm text-gray-300 text-center py-6">ไม่มีของที่พักไว้</p>}
          {!loading && items.length > 0 && (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-xs text-gray-400 uppercase border-b border-gray-100">
                  <th className="text-left py-2">Tag</th>
                  <th className="text-left py-2">รูป</th>
                  <th className="text-left py-2">Tracking JP</th>
                  <th className="text-right py-2">น้ำหนัก</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {items.map((it) => (
                  <tr key={it.uuid} className="border-b border-gray-50">
                    <td className="py-2 font-mono font-semibold text-amber-700">{it.holdTag}</td>
                    <td className="py-2">
                      {it.shipImageUrl
                        ? <img
                            src={it.shipImageUrl}
                            alt=""
                            onClick={() => setZoomUrl(it.shipImageUrl)}
                            className="w-12 h-12 object-cover rounded border border-gray-200 cursor-zoom-in"
                          />
                        : <span className="text-gray-300 text-xs">—</span>}
                    </td>
                    <td className="py-2 font-mono text-gray-600">
                      {it.trackingJp || <span className="text-gray-400 italic">ไม่มีเลขพัสดุ</span>}
                    </td>
                    <td className="py-2 text-right text-gray-600">{it.weight != null ? `${it.weight} kg` : '-'}</td>
                    <td className="py-2 text-right whitespace-nowrap">
                      <button
                        onClick={() => openFind(it)}
                        disabled={busyUuid === it.uuid}
                        className="px-3 py-1 mr-2 bg-white border border-indigo-300 hover:border-indigo-500 text-indigo-700 disabled:opacity-50 text-xs rounded-lg"
                      >
                        ค้นหา order + ย้าย
                      </button>
                      <button
                        onClick={() => promote(it)}
                        disabled={busyUuid === it.uuid}
                        className="px-3 py-1 mr-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white text-xs rounded-lg"
                      >
                        {busyUuid === it.uuid ? '...' : 'ย้าย (ไม่ระบุชื่อ)'}
                      </button>
                      <button
                        onClick={() => removeHeld(it)}
                        disabled={busyUuid === it.uuid}
                        className="px-3 py-1 bg-white border border-red-300 hover:border-red-500 text-red-600 disabled:opacity-50 text-xs rounded-lg"
                        title="ลบ (สแกนผิด/กรอกผิด)"
                      >
                        🗑️
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        </>
        )}

        {tab === 'pending' && (
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {pendingLoading && <p className="text-sm text-gray-400 text-center py-6">กำลังโหลด…</p>}
          {!pendingLoading && pendingItems.length === 0 && (
            <p className="text-sm text-gray-300 text-center py-6">ไม่มีรายการรอพิมพ์</p>
          )}
          {!pendingLoading && pendingItems.length > 0 && (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-xs text-gray-400 uppercase border-b border-gray-100">
                  <th className="text-left py-2">ป้ายเดิม</th>
                  <th className="text-left py-2">กล่องใหม่</th>
                  <th className="text-left py-2">รอบเรือ</th>
                  <th className="text-left py-2">ชื่อ</th>
                  <th className="text-left py-2">Tracking JP</th>
                  <th className="text-right py-2">น้ำหนัก</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {pendingItems.map((it) => (
                  <tr key={it.uuid} className="border-b border-gray-50">
                    <td className="py-2 font-mono font-semibold text-amber-700">{it.holdTag || '-'}</td>
                    <td className="py-2 font-semibold text-gray-800">#{it.boxNo}</td>
                    <td className="py-2 text-gray-600">{it.shipDate ? formatDate(it.shipDate) : '-'}</td>
                    <td className="py-2 text-gray-800">{it.smileNameInput || <span className="text-gray-300">ไม่ระบุชื่อ</span>}</td>
                    <td className="py-2 font-mono text-gray-600">
                      {it.trackingJp || <span className="text-gray-400 italic">ไม่มีเลขพัสดุ</span>}
                    </td>
                    <td className="py-2 text-right text-gray-600">{it.weight != null ? `${it.weight} kg` : '-'}</td>
                    <td className="py-2 text-right whitespace-nowrap">
                      <button
                        onClick={() => printPending(it)}
                        disabled={busyUuid === it.uuid}
                        className="px-3 py-1 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white text-xs rounded-lg"
                      >
                        {busyUuid === it.uuid ? '...' : '🖨️ พิมพ์'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        )}
      </div>

      {findForItem && (
        <FindOrderModal
          profiles={profiles}
          profilesReady={profilesReady}
          onSelect={(order) => { const it = findForItem; setFindForItem(null); promote(it, order); }}
          onClose={() => setFindForItem(null)}
        />
      )}

      {zoomUrl && <ImageModal src={zoomUrl} onClose={() => setZoomUrl(null)} />}
    </div>
  );
};

export default HeldItemsModal;
